import { Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * A7 · LA CAJA DE BÚSQUEDA, una sola para todas las pantallas.
 *
 * La auditoría A0 encontró **el mismo buscador escrito cuatro veces** —Clientes,
 * Contactos, CRM y la lista de conversaciones—, con clases distintas, textos de
 * ayuda distintos y ninguno con forma de borrar lo escrito. No es solo código
 * repetido: es que la misma acción se ve y se comporta distinto según dónde
 * estés, y eso se paga cada día en dudas chiquitas.
 *
 * Lo que agrega respecto de las cuatro versiones anteriores, y por qué:
 *
 * - **Una X para limpiar.** Ninguna la tenía. Borrar a mano un teléfono largo
 *   para volver a ver todo es de las cosas que se hacen veinte veces al día.
 * - **`type="search"`**, que le da a los navegadores y lectores de pantalla el
 *   dato de qué es esto.
 * - **Escape limpia.** Lo mismo que hace la X, sin soltar el teclado.
 */
export function CajaDeBusqueda({
  valor,
  onChange,
  placeholder = 'Buscar por nombre o teléfono…',
  className,
  autoFocus,
}: {
  valor: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}) {
  return (
    <div className={cn('relative', className)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        type="search"
        value={valor}
        autoFocus={autoFocus}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onChange('');
        }}
        placeholder={placeholder}
        className={cn(
          // 21-oct, macOS 27: campo de búsqueda en cápsula gris; 44 y letra 16 en el teléfono.
          'mac-busqueda',
          valor ? 'pr-9' : 'pr-3',
          // El navegador dibuja su propia X en `type=search` y quedarían dos.
          '[&::-webkit-search-cancel-button]:hidden',
        )}
      />
      {valor && (
        <button
          type="button"
          onClick={() => onChange('')}
          title="Limpiar (Esc)"
          aria-label="Limpiar la búsqueda"
          className="absolute right-1 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 lateral:right-1.5 lateral:h-6 lateral:w-6"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
